import analytics from '@/@core/utils/analytics'
import { kpEndpoint, kpParams, kpRequest } from '@/axios'
import constants, { PRODUCT_FIELD_MAP } from '@/constants'
import { cloneDeep, debounce, isEqual } from 'lodash'

export const INITIALIZE_EDIT_STORE = 'INITIALIZE_EDIT_STORE'
export const SET_PRODUCT_IMAGES = 'SET_PRODUCT_IMAGES'
export const UPLOAD_NEW_PRODUCT_IMAGE = 'UPLOAD_NEW_PRODUCT_IMAGE'
export const UPDATE_PRODUCT_INFO = 'UPDATE_PRODUCT_INFO'
export const RESET_EDIT_STORE = 'RESET_EDIT_STORE'
export const UPDATE_PRODUCT = 'UPDATE_PRODUCT'
export const GET_PRODUCT_ORDERS = 'GET_PRODUCT_ORDERS'
export const UPDATE_ORDER_PRODUCT_PRICES = 'UPDATE_ORDER_PRODUCT_PRICES'
export const CHECK_PRODUCT_ORDERS = 'CHECK_PRODUCT_ORDERS'
export const ITEMS_UPDATED = 'ITEMS_UPDATED'

export const state = {
  savedState: {},
  newData: {},
  changedFields: [],
  productImages: [],
}

const debouncedTrackProductEdit = debounce(trackProductEdit, 500)

function trackProductEdit(state) {
  analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CHANGES_FORM_FIELDS, {
    productId: state.savedState._id,
    collectionId: state.savedState.collectionId,
    formData: {
      ...state.newData,
      images: state.productImages.map(image => image.url),
    },
  })
}

function validateShippingDates(payload, savedState) {
  const { shippingStartDate, shippingEndDate } = savedState
  const { shippingStartDate: newShippingStartDate, shippingEndDate: newShippingEndDate } = payload

  if (!newShippingStartDate && !newShippingEndDate) {
    return { areShippingDatesValid: true, errorMessage: null }
  }

  const startDate = newShippingStartDate || shippingStartDate
  const endDate = newShippingEndDate || shippingEndDate
  if (!startDate || !endDate) {
    return { areShippingDatesValid: false, errorMessage: 'Both shipping start and end dates are required.' }
  }
  const areShippingDatesValid = new Date(startDate) < new Date(endDate)
  const errorMessage = areShippingDatesValid ? null : 'Shipping start date must be earlier than shipping end date.'
  return { areShippingDatesValid, errorMessage }
}

// Matches ISO-8601 date and date-time prefixes (YYYY-MM-DD optionally followed
// by a time component). Used to canonicalize date fields so a Date object
// produced by the date input compares equal to the ISO string returned by the
// API.
const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/

const normalize = val => {
  if (val instanceof Date) {
    const time = val.getTime()
    return Number.isNaN(time) ? val : time
  }
  if (typeof val === 'string') {
    if (val !== '' && !isNaN(val)) return Number(val)
    if (ISO_DATE_PREFIX.test(val)) {
      const time = new Date(val).getTime()
      if (!Number.isNaN(time)) return time
    }
  }
  return val
}

const mutations = {
  [INITIALIZE_EDIT_STORE](state, productInfo) {
    const {
      images,
    } = productInfo
    state.savedState = productInfo
    state.newData = {
      ...productInfo,
      wholesalePrice: Number(productInfo.wholesalePrice) || null,
      retailPrice: Number(productInfo.retailPrice) || null,
    }
    state.productImages = images.map((image, index) => ({
      url: image,
      isLocal: false,
      index,
    }))
    state.changedFields = []
  },
  [SET_PRODUCT_IMAGES](state, newImages) {
    state.productImages = newImages
    state.changedFields = [...state.changedFields, 'images']
    debouncedTrackProductEdit(state)
  },
  [UPDATE_PRODUCT_INFO](state, { fieldName, newValue }) {
    state.newData[fieldName] = newValue

    const originalValue = cloneDeep(state.savedState[fieldName])
    const isChanged = !isEqual(normalize(originalValue), normalize(newValue))
    const isInChangedFields = state.changedFields.includes(fieldName)

    if (isChanged && !isInChangedFields) {
      state.changedFields.push(fieldName)
    } else if (!isChanged && isInChangedFields) {
      // Remove every occurrence — defensive against any caller pushing dupes.
      state.changedFields = state.changedFields.filter(f => f !== fieldName)
    }
    debouncedTrackProductEdit(state)
  },
  [ITEMS_UPDATED](state) {
    if (!state.changedFields.includes('items')) {
      state.changedFields.push('items')
    }
  },
  [RESET_EDIT_STORE](state) {
    state.savedState = {}
    state.newData = {}
    state.changedFields = []
    state.productImages = []
  },
}

const actions = {
  async [UPLOAD_NEW_PRODUCT_IMAGE]({ state, commit }, { index, formData }) {
    const { collectionId, uniqueId } = state.savedState
    const currentImages = [...state.productImages]
    const response = await kpRequest({ ...kpParams(kpEndpoint.product.uploadProductImages, encodeURIComponent(collectionId), encodeURIComponent(uniqueId), `${index}`), payload: formData })
    if (response.data) {
      const newImages = currentImages.map(image => (image.index === index ? {
        url: `${response.data.data.location}?t=${Date.now()}`,
        isLocal: false,
        index,
      } : image))
      commit(SET_PRODUCT_IMAGES, newImages)
      return response.data.data.location
    }
    return null
  },
  async [UPDATE_PRODUCT]({ state }) {
    const { _id } = state.savedState
    const payload = {}
    state.changedFields.forEach(field => {
      switch (field) {
        case PRODUCT_FIELD_MAP.DEPARTMENT:
        case PRODUCT_FIELD_MAP.CATEGORY:
        case PRODUCT_FIELD_MAP.SUB_CATEGORY:
        case PRODUCT_FIELD_MAP.COUNTRY:
        case PRODUCT_FIELD_MAP.GENDER:
        case PRODUCT_FIELD_MAP.STATUS:
          payload[field] = state.newData[field][0]
          break
        case PRODUCT_FIELD_MAP.IS_CARTON:
          payload[field] = state.newData[field]
          break
        case PRODUCT_FIELD_MAP.IMAGES:
          payload[field] = state.productImages.map(image => image.url)
          break
        default:
          payload[field] = state.newData[field]
      }
    })
    const isCarton = payload[PRODUCT_FIELD_MAP.IS_CARTON] || state.newData[PRODUCT_FIELD_MAP.IS_CARTON]
    const { areShippingDatesValid, errorMessage } = validateShippingDates(payload, state.savedState)
    if (!areShippingDatesValid) {
      throw new Error(errorMessage)
    }
    payload.items = state.newData.items.map(item => ({
      ...item,
      value: isCarton ? Number(item.value) || 0 : 0,
    }))
    const response = await kpRequest({
      ...kpParams(kpEndpoint.product.updateProduct, _id),
      payload,
    })
    if (response.data) {
      return response.data
    }
    return null
  },
  async [GET_PRODUCT_ORDERS](_, { productId }) {
    if (!productId) return null

    const response = await kpRequest({
      ...kpEndpoint.order.get,
      params: { productId, allOrders: true },
    })

    // Return the orders array from the response
    return response.data?.data?.orders || []
  },
  async [CHECK_PRODUCT_ORDERS](_, { productId }) {
    if (!productId) return false

    try {
      const response = await kpRequest({
        ...kpEndpoint.order.get,
        params: { productId },
      })
      return !!response.data?.data?.status
    } catch (error) {
      console.error('Error checking product orders:', error)
      return false
    }
  },
  async [UPDATE_ORDER_PRODUCT_PRICES](_, payload) {
    if (!payload || !Array.isArray(payload.orderIds) || !payload.products) {
      console.warn('Invalid payload for updating order product prices')
      return null
    }

    const response = await kpRequest({
      ...kpParams(kpEndpoint.order.updateOrderProductPrices),
      payload,
    })

    return response.data || null
  },
}

export default {
  state,
  mutations,
  actions,
}
